import { useEffect, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import type { DailyCall } from '@daily-co/daily-js';
import {
	extractDailyJoinErrorDetails,
	shouldHandleCallJoinError,
} from '@/components/session-call/sessionCallJoinUtils';
import { buildDailyPublicRoomUrl } from '@/utils/buildDailyPublicRoomUrl';

type UsePublicSessionDailyCallParams = {
	providerId: string | undefined;
	userName: string | null;
};

export function usePublicSessionDailyCall({
	providerId,
	userName,
}: UsePublicSessionDailyCallParams) {
	const containerRef = useRef<HTMLDivElement>(null);
	const callFrameRef = useRef<DailyCall | null>(null);
	const lastDailyErrorMessageRef = useRef<string | null>(null);

	const [isJoining, setIsJoining] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		const trimmedProviderId = providerId?.trim();
		if (!trimmedProviderId) {
			setIsJoining(false);
			setErrorMessage('Link inválido: sala não informada.');
			return;
		}

		let cancelled = false;
		setIsJoining(true);
		setErrorMessage(null);
		lastDailyErrorMessageRef.current = null;

		const destroyCurrentCall = async () => {
			const call = callFrameRef.current ?? DailyIframe.getCallInstance();
			callFrameRef.current = null;

			if (!call || call.isDestroyed()) {
				return;
			}

			try {
				await call.destroy();
			} catch {
				/* cleanup defensivo */
			}
		};

		const showJoinError = (
			error: unknown,
			fallbackMessage?: string | null,
		) => {
			const { message } = extractDailyJoinErrorDetails(
				error,
				fallbackMessage,
			);
			setIsJoining(false);
			setErrorMessage(message);
			void destroyCurrentCall();
		};

		const setupCall = async () => {
			try {
				await destroyCurrentCall();
				if (cancelled) return;

				const container = containerRef.current;
				if (!container) {
					throw new Error('Container da chamada indisponível.');
				}

				const roomUrl = buildDailyPublicRoomUrl(trimmedProviderId);
				container.replaceChildren();

				const callFrame = DailyIframe.createFrame(container, {
					showLeaveButton: true,
					showFullscreenButton: true,
					iframeStyle: {
						position: 'absolute',
						top: '0',
						left: '0',
						width: '100%',
						height: '100%',
						border: '0',
					},
				});

				callFrameRef.current = callFrame;

				callFrame.on('error', (event) => {
					const message =
						(
							event as {
								errorMsg?: string;
								error?: { msg?: string };
							}
						).errorMsg ??
						(event as { error?: { msg?: string } }).error?.msg ??
						null;
					lastDailyErrorMessageRef.current = message;
					if (cancelled || !shouldHandleCallJoinError(callFrame)) {
						return;
					}
					showJoinError(event, message);
				});

				if (!cancelled) {
					setIsJoining(false);
				}

				await callFrame.join({
					url: roomUrl,
					...(userName ? { userName } : {}),
				});
			} catch (error) {
				if (!cancelled) {
					showJoinError(error, lastDailyErrorMessageRef.current);
				}
			}
		};

		void setupCall();

		return () => {
			cancelled = true;
			void destroyCurrentCall();
		};
	}, [providerId, userName]);

	return {
		containerRef,
		isJoining,
		errorMessage,
	};
}
